import { fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { baseApi } from "./baseApi";
import { API_BASE_URL } from "../../config/env";
import type {
  BadgeCatalogResponse,
  BadgeSweepRequest,
  BadgeSweepResponse,
  CollectBadgesRequest,
  CollectBadgesResponse,
  MyBadgeRankResponse,
  MyBadgesResponse,
  RecentBadgesResponse,
  SetBadgePinsRequest,
  SetBadgePinsResponse,
  ShareBadgeRequest,
  ShareBadgeResponse,
  SharedBadgeResponse,
  UnrevealedResponse,
  UserBadgesResponse,
} from "../../features/badges/types";

// ---------- legacy shapes (kept for one release; the old endpoints still answer) ----------

export interface BadgeDefinition {
  _id: string;
  code: string;
  name: string;
  description?: string;
  value?: number;
  color?: string;
  isUnique?: boolean;
}

export interface UncollectedBadge {
  _id: string;
  badge: BadgeDefinition;
  collected: boolean;
  dateEarned?: string;
}

export interface UserBadge {
  _id: string;
  badge: BadgeDefinition;
  collected: boolean;
  dateEarned?: string;
}

// ---------- paths (relative to API_BASE_URL, which already ends in /api) ----------

const enc = encodeURIComponent;

export const badgesPaths = {
  // legacy
  unconfirmed: "/badges/unconfirmed",
  all: "/badges/all",
  confirm: "/badges/confirm",
  // badges-3
  me: "/badges/me",
  catalog: "/badges/catalog",
  unrevealed: "/badges/me/unrevealed",
  collect: "/badges/me/collect",
  pins: "/badges/me/pins",
  rank: "/badges/me/rank",
  share: "/badges/me/share",
  shared: (shareId: string) => `/badges/share/${enc(shareId)}`,
  user: (username: string) => `/badges/user/${enc(username)}`,
  recent: (days = 7) => `/badges/recent?days=${enc(String(days))}`,
  adminSweep: "/badges/admin/sweep",
} as const;

// The public share page must work logged out, so its one request skips the auth
// header and the refresh-on-401 wrapper altogether.
const publicBaseQuery = fetchBaseQuery({ baseUrl: API_BASE_URL });

export const badgesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // ----- legacy -----
    getUncollectedBadges: builder.query<UncollectedBadge[], void>({
      query: () => badgesPaths.unconfirmed,
      providesTags: ["Badges"],
    }),
    getAllBadges: builder.query<UserBadge[], void>({
      query: () => badgesPaths.all,
      providesTags: ["Badges"],
    }),
    collectBadge: builder.mutation<void, string>({
      query: (badgeCode) => ({
        url: badgesPaths.confirm,
        method: "POST",
        body: { badgeCode },
      }),
      invalidatesTags: ["Badges"],
    }),

    // ----- badges-3 -----
    getMyBadges: builder.query<MyBadgesResponse, void>({
      query: () => badgesPaths.me,
      providesTags: ["Badges"],
    }),
    getBadgeCatalog: builder.query<BadgeCatalogResponse, void>({
      query: () => badgesPaths.catalog,
      providesTags: ["Badges"],
    }),
    getUnrevealedBadges: builder.query<UnrevealedResponse, void>({
      query: () => badgesPaths.unrevealed,
      providesTags: ["Badges"],
    }),
    collectBadgeCopies: builder.mutation<CollectBadgesResponse, CollectBadgesRequest>({
      query: (body) => ({ url: badgesPaths.collect, method: "POST", body }),
      invalidatesTags: ["Badges"],
    }),
    setBadgePins: builder.mutation<SetBadgePinsResponse, SetBadgePinsRequest>({
      query: (body) => ({ url: badgesPaths.pins, method: "PUT", body }),
      invalidatesTags: ["Badges"],
    }),
    getMyBadgeRank: builder.query<MyBadgeRankResponse, void>({
      query: () => badgesPaths.rank,
      providesTags: ["Badges"],
    }),
    // Creating a share link changes no badge data, so it invalidates nothing.
    shareBadge: builder.mutation<ShareBadgeResponse, ShareBadgeRequest>({
      query: (body) => ({ url: badgesPaths.share, method: "POST", body }),
    }),
    getSharedBadge: builder.query<SharedBadgeResponse, string>({
      async queryFn(shareId, api, extraOptions) {
        const result = await publicBaseQuery(badgesPaths.shared(shareId), api, extraOptions);
        if (result.error) return { error: result.error };
        return { data: result.data as SharedBadgeResponse };
      },
    }),
    getUserBadges: builder.query<UserBadgesResponse, string>({
      query: (username) => badgesPaths.user(username),
      providesTags: ["Badges"],
    }),
    getRecentBadges: builder.query<RecentBadgesResponse, number | void>({
      query: (days) => badgesPaths.recent(typeof days === "number" ? days : 7),
      providesTags: ["Badges"],
    }),
    // A dry run writes nothing; a real sweep can award copies to anyone, the caller included.
    runBadgeSweep: builder.mutation<BadgeSweepResponse, BadgeSweepRequest | void>({
      query: (body) => ({ url: badgesPaths.adminSweep, method: "POST", body: body ?? {} }),
      invalidatesTags: (_result, _error, arg) => (arg && arg.dryRun ? [] : ["Badges"]),
    }),
  }),
  overrideExisting: false,
});

export const {
  // legacy
  useGetUncollectedBadgesQuery,
  useGetAllBadgesQuery,
  useCollectBadgeMutation,
  // badges-3
  useGetMyBadgesQuery,
  useGetBadgeCatalogQuery,
  useGetUnrevealedBadgesQuery,
  useCollectBadgeCopiesMutation,
  useSetBadgePinsMutation,
  useGetMyBadgeRankQuery,
  useShareBadgeMutation,
  useGetSharedBadgeQuery,
  useGetUserBadgesQuery,
  useGetRecentBadgesQuery,
  useRunBadgeSweepMutation,
} = badgesApi;
